import React from 'react'
import classNames from 'classnames'
import dayjs from 'dayjs'
import isToday from 'dayjs/plugin/isToday'
import relativeTime from 'dayjs/plugin/relativeTime'
import {
    IoCalendarOutline,
    IoFlagOutline,
    IoFlameOutline,
    IoFlashOutline,
    IoGiftOutline,
    IoHeartOutline,
    IoInformationCircleOutline,
    IoMedkitOutline,
    IoRestaurantOutline,
    IoRibbonOutline,
    IoWaterOutline,
} from 'react-icons/io5'

// Ô giới thiệu ngắn đầu mỗi màn hình con của "Kỷ niệm" - trả lời 3 câu: ĐÂY LÀ GÌ, LÀM ĐƯỢC GÌ,
// NHẬN VỀ GÌ, để user không phải đoán khi lần đầu vào 1 trong các popup/tab (MomentsPanel,
// SendMomentNoticeModal, CreateCustomMomentButton...) - dùng chung 1 style, đúng pattern info-box đã
// có ở FamilyTreeSubTab (bg-family-50 rounded-xl text-xs).
export const InfoBanner = ({children}) => (
    <div className="p-3 flex items-start gap-2 rounded-xl bg-family-50 text-xs text-family-700 leading-snug">
        <IoInformationCircleOutline size={15} className="flex-shrink-0 mt-0.5"/>
        <span>{children}</span>
    </div>
)

// isToday/relativeTime - đã dùng sẵn ở nhiều nơi khác trong app (src/helpers/dateUtils.js,
// datetimeHelpers.js, modules/local/index.js - dayjs.locale() được đồng bộ theo ngôn ngữ app ở đó) -
// extend lại ở đây là AN TOÀN (dayjs cho phép extend nhiều lần), không tự chế lại phần dịch/số ít số
// nhiều mà để thư viện lo (locale vi/en/fr đã có sẵn qua dayjs/locale/*).
dayjs.extend(isToday)
dayjs.extend(relativeTime)

// 9 loại "kỷ niệm" đủ - khớp Common::$familyMomentTypes (BE) - birthday/first_communion/
// confirmation/holy_orders/anointing_of_the_sick trước đây bị thiếu hoàn toàn ở widget "sắp đến"
// (BE chỉ tính patron_saint/baptism/matrimony/death_anniversary), đã bổ sung đủ ở
// FamilyMomentHelper::computeUpcomingMoments(). Tách khỏi FamilyBooks.js (thay vì định nghĩa tại
// chỗ) để MomentsPanel.js/SendMomentNoticeModal.js dùng chung được mà không tạo circular import
// (2 file đó vốn được FamilyBooks.js import ngược lại).
// Mỗi loại 1 màu riêng (đủ 9 màu tách bạch nhau trong palette tailwind.config.js) để quét nhanh
// bằng mắt giữa các dòng trong list - trước đây TẤT CẢ loại dùng chung 1 màu family nên rất khó phân
// biệt. Class ghi literal đầy đủ (không nối chuỗi động kiểu `bg-${color}-50`) theo đúng convention đã
// có ở getPrayerCategoryCode.js (PRAYER_COLOR_MAP) - Tailwind purge chỉ giữ lại class xuất hiện
// nguyên văn trong source, ghép động sẽ bị xoá mất khi build.
// yearsLabel - riêng birthday tính TUỔI (n years old: "{n} tuổi"), các loại còn lại đều là kỷ niệm N
// năm của 1 sự kiện đã qua (rửa tội/hôn phối/giỗ/thêm sức...) nên dùng chung "n years anniversary"
// ("{n} năm") - khai báo tường minh từng loại thay vì if riêng ở chỗ render để dễ soát/sửa khi thêm
// loại mới.
export const MOMENT_META = {
    birthday: {icon: IoGiftOutline, label: 'moment_birthday_label', iconBg: 'bg-amber-50', iconColor: 'text-amber-600', labelColor: 'text-amber-600', yearsLabel: 'n years old'},
    patron_saint: {icon: IoFlagOutline, label: 'moment_patron_saint_label', iconBg: 'bg-violet-50', iconColor: 'text-violet-600', labelColor: 'text-violet-600', yearsLabel: 'n years anniversary'},
    death_anniversary: {icon: IoFlameOutline, label: 'moment_death_label', iconBg: 'bg-slate-100', iconColor: 'text-slate-600', labelColor: 'text-slate-600', yearsLabel: 'n years anniversary'},
    baptism: {icon: IoWaterOutline, label: 'moment_baptism_label', iconBg: 'bg-sky-50', iconColor: 'text-sky-600', labelColor: 'text-sky-600', yearsLabel: 'n years anniversary'},
    first_communion: {icon: IoRestaurantOutline, label: 'moment_first_communion_label', iconBg: 'bg-pink-50', iconColor: 'text-pink-600', labelColor: 'text-pink-600', yearsLabel: 'n years anniversary'},
    confirmation: {icon: IoFlashOutline, label: 'moment_confirmation_label', iconBg: 'bg-orange-50', iconColor: 'text-orange-600', labelColor: 'text-orange-600', yearsLabel: 'n years anniversary'},
    matrimony: {icon: IoHeartOutline, label: 'moment_matrimony_label', iconBg: 'bg-rose-50', iconColor: 'text-rose-600', labelColor: 'text-rose-600', yearsLabel: 'n years anniversary'},
    holy_orders: {icon: IoRibbonOutline, label: 'moment_holy_orders_label', iconBg: 'bg-indigo-50', iconColor: 'text-indigo-600', labelColor: 'text-indigo-600', yearsLabel: 'n years anniversary'},
    anointing_of_the_sick: {icon: IoMedkitOutline, label: 'moment_anointing_label', iconBg: 'bg-emerald-50', iconColor: 'text-emerald-600', labelColor: 'text-emerald-600', yearsLabel: 'n years anniversary'},
}
export const MOMENT_TYPES_ORDERED = Object.keys(MOMENT_META)
const DEFAULT_MOMENT_META = {iconBg: 'bg-family-50', iconColor: 'text-family-700', labelColor: 'text-family-600', yearsLabel: 'n years anniversary'}

// Đếm ngược tới ngày diễn ra - dùng thẳng dayjs relativeTime (target.from(now), CÙNG hàm đang dùng
// cho .fromNow() ở khắp nơi khác trong app - WorkspaceModerationList, renderHomeFeed...) thay vì tự
// tính diff/số ít-nhiều/dịch tay - locale (vi/en/fr) do dayjs.locale() lo, đồng bộ theo ngôn ngữ app
// (xem modules/local/index.js). So sánh theo NGÀY (startOf day cả 2 vế) để không lệch giờ trong ngày.
// isToday() (dayjs plugin) là trường hợp riêng vì relativeTime không có khái niệm "hôm nay" - diff=0
// giữa 2 mốc startOf day sẽ ra "a few seconds"/"vài giây" chứ không phải "hôm nay".
const getCountdownLabel = (dateStr, t) => {
    if (!dateStr) return null
    const target = dayjs(dateStr)
    if (target.isToday()) return t('moment_countdown_today')
    if (target.isBefore(dayjs(), 'day')) return null
    return target.startOf('day').from(dayjs().startOf('day'))
}

// `action` (optional) - render nút hành động bên phải (vd "Gửi thông báo" ở MomentsPanel, trang Kỷ
// niệm). `onClick` giờ gắn vào TỪNG TÊN riêng (không phải cả hàng) - occurrence bị gom (cùng ngày +
// cùng loại, xem FamilyMomentHelper::groupByDateType()) liệt kê NHIỀU người, bấm đúng tên nào phải mở
// đúng hồ sơ người đó chứ không phải luôn người đầu tiên trong nhóm.
export const MomentRow = ({item, onClick, action, t}) => {
    const meta = MOMENT_META[item.type] || {icon: IoCalendarOutline, label: item.type, ...DEFAULT_MOMENT_META}
    const Icon = meta.icon
    const displayDate = item.date ? dayjs(item.date).format('DD/MM/YYYY') : ''
    // "N năm" - chỉ tính được khi có source_date (mốc gốc): sinh nhật, rửa tội, hôn phối, giỗ. Bổn
    // mạng lặp hàng năm không có mốc gốc nên không tính.
    const years = item.source_date && item.date ? dayjs(item.date).year() - dayjs(item.source_date).year() : null
    const countdown = getCountdownLabel(item.date, t)
    // item.members ({id, name}[]) - BE trả cho dòng đã gộp nhiều người (teaser Trang Chủ). Item
    // ungrouped (MomentsPanel, luôn 1 người/dòng) không có field này -> tự dựng mảng 1 phần tử từ
    // member_id/member_name cũ để dùng chung 1 đường render.
    const members = item.members || [{id: item.member_id, name: item.member_name}]

    return (
        <div className="p-3 flex items-center gap-3 background border border-color-50 rounded-xl">
            <div className="flex items-center gap-3 flex-1 min-w-0">
                <div style={{width: 36, height: 36}} className={classNames('flex flex-center rounded-lg flex-shrink-0', meta.iconBg)}>
                    <Icon size={16} className={meta.iconColor}/>
                </div>
                <div className="flex-1 min-w-0">
                    {/* Ngày lên đầu (thông tin chính - "khi nào"), countdown đi kèm ngay cạnh vì cùng
                        trả lời "còn bao lâu" - loại kỷ niệm dời xuống dòng cuối làm chú thích, theo
                        đúng thứ tự ngày → tên → loại/số năm. */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-bold text-sm text-color-000">{displayDate}</span>
                        {countdown && (
                            <span className="text-2xs font-semibold text-rose-600">{countdown}</span>
                        )}
                    </div>
                    {/* Mỗi người 1 dòng riêng (thay vì 1 chuỗi tên nối dấu phẩy) - bấm ĐÚNG tên mở hồ
                        sơ người đó, không còn luôn mở người đầu nhóm. */}
                    <div className="flex flex-col">
                        {members.map((m, i) => (
                            <span
                                key={m.id ?? i}
                                onClick={() => onClick && onClick({...item, member_id: m.id, member_name: m.name})}
                                className={classNames(
                                    'font-semibold text-sm text-color-000 break-words',
                                    onClick && 'cursor-pointer hover:underline hover:text-family-700'
                                )}>
                                {m.name}
                            </span>
                        ))}
                    </div>
                    <span className={classNames('text-2xs font-bold uppercase tracking-wider', meta.labelColor)}>
                        {t(meta.label)}
                        {years != null && years > 0 && ` · ${t(meta.yearsLabel, {n: years})}`}
                    </span>
                </div>
            </div>
            {action}
        </div>
    )
}
